// AB#2382 — the field help affordance: a muted info glyph beside a field label that reveals the
// entity's authored `helpText` on hover, keyboard focus or tap.
//
// Why a glyph and not always-visible helper text: a permanent second line under every input roughly
// doubles the height of the dense screens this renderer exists to draw (CustomerAccount is 63 fields
// laid out two-up). The hint has to be available without being present, so it is opt-in per glance.
//
// The component is deliberately self-contained — no portal, no positioning library, no external
// tooltip dependency (the package has none). It renders nothing at all when the field has no
// helpText, so it costs untagged fields nothing.
import { useEffect, useId, useRef, useState } from 'react'

export interface FieldHintProps {
    /** The authored hint. When empty/absent the component renders nothing. */
    text?: string | undefined
    /** The field's visible label, used to name the trigger for screen readers. */
    label?: string | undefined
}

export function FieldHint({ text, label }: FieldHintProps) {
    const [open, setOpen] = useState(false)
    const id = useId()
    const wrapRef = useRef<HTMLSpanElement | null>(null)

    // Escape closes an open hint, and a pointer-down anywhere outside dismisses it. Both matter for
    // the tap-to-toggle path, where there is no pointer-leave to close it again.
    useEffect(() => {
        if (!open) { return }
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false) } }
        const onDown = (e: Event) => {
            if (!wrapRef.current?.contains(e.target as Node)) { setOpen(false) }
        }
        document.addEventListener('keydown', onKey)
        document.addEventListener('pointerdown', onDown)
        return () => {
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('pointerdown', onDown)
        }
    }, [open])

    const hint = text?.trim()
    if (!hint) { return null }

    return (
        <span
            ref={wrapRef}
            className="relative inline-flex align-middle"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <button
                type="button"
                // The label is already adjacent visually, but a screen reader reaching this button out
                // of context needs to know which field it belongs to.
                aria-label={label ? `About ${label}` : 'About this field'}
                aria-describedby={id}
                aria-expanded={open}
                className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border
                           border-neutral-300 text-[9px] font-semibold leading-none text-neutral-400
                           transition-colors hover:border-neutral-500 hover:text-neutral-600
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                           dark:border-slate-600 dark:text-slate-500 dark:hover:border-slate-400
                           dark:hover:text-slate-300"
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                onClick={e => { e.preventDefault(); e.stopPropagation(); setOpen(o => !o) }}
            >
                i
            </button>
            {/* Always in the DOM so assistive tech can read the description via aria-describedby;
                only painted when open. */}
            <span
                id={id}
                role="tooltip"
                className={`${open ? 'block' : 'hidden'} absolute left-0 top-5 z-50 w-64 rounded-md
                            bg-neutral-800 px-2.5 py-1.5 text-xs font-normal leading-snug text-white
                            shadow-lg dark:bg-slate-700`}
            >
                {hint}
            </span>
        </span>
    )
}
